import { createContext, useContext, FC, useMemo, useState, useEffect } from 'react';
import { Moment } from 'moment';
import { usePaginationContext } from 'utils/contexts/pagination';
import noop from 'utils/noop';
import { DEFAULT_QUERY, HighestConversionCampaign } from '../../domain';
import { useConversionInsightsQuery } from '../hooks';
import { useInsightsQueryContext } from './InsightsQueryContext';
import { useAngularListSegmentContext } from '../../contexts/AngularListSegmentContext';

export type ConversionInsightsState = {
  highestConvertingCampaigns: HighestConversionCampaign[];
  currentConversionEvent: string | null;
  totalCampaigns: number;
  overallConversionRate: number;
  updateConversion: (conversion: string | null) => void;
  isLoading: boolean;
};

export const defaultConversionState = {
  highestConvertingCampaigns: [],
  currentConversionEvent: null,
  totalCampaigns: 0,
  overallConversionRate: 0,
  updateConversion: noop,
  isLoading: false,
};

export const ConversionInsightsContext =
  createContext<ConversionInsightsState>(defaultConversionState);
export const useConversionInsightsContext = () => useContext(ConversionInsightsContext);

export const ConversionInsightsContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { listDetails } = useAngularListSegmentContext();
  const { startDate, endDate } = useInsightsQueryContext();
  const { currentPage, pageSize } = usePaginationContext();
  const [eventName, updateConversion] = useState<string | null>(null);

  const conversionParams = listDetails
    ? {
        ...listDetails,
        startDate: (startDate as Moment)?.toISOString(),
        endDate: (endDate as Moment)?.toISOString(),
      }
    : DEFAULT_QUERY;

  const params = useMemo(
    () => ({
      ...conversionParams,
      page: currentPage,
      pageSize,
      eventName,
    }),
    [listDetails, startDate, endDate, currentPage, pageSize, eventName],
  );

  const {
    data: { highestConvertingCampaigns, totalCampaigns, overallConversionRate } = {},
    refetch,
    isLoading,
  } = useConversionInsightsQuery(params);

  const cachedHighestConvertingCampaigns = useMemo(
    () => highestConvertingCampaigns ?? [],
    [highestConvertingCampaigns],
  );

  useEffect(() => {
    if (params?.emailListId) {
      refetch();
    }
  }, [params]);

  const value = {
    highestConvertingCampaigns: cachedHighestConvertingCampaigns,
    totalCampaigns: totalCampaigns ?? 0,
    overallConversionRate: overallConversionRate ?? 0,
    currentConversionEvent: eventName,
    updateConversion,
    isLoading,
  };

  return (
    <ConversionInsightsContext.Provider value={value}>
      {children}
    </ConversionInsightsContext.Provider>
  );
};
